/*
  Click Fencing calculator lead capture + price-shown conversion.
  Added 2026-09-04. See domains/empire-advertising/references/full-funnel-audit-2026-09-04.md

  WHY THIS EXISTS

  Measured on live 2026-09-04. The four calculators end on "Review & Checkout" with a real
  price on screen and exactly these controls: Add to Cart, Back, Start again, and a small
  "Send us your plans" pill. There was no way to leave an email address, so the only thing a
  customer who is not ready to spend $545 today could do was leave.

  Two separate defects put it there, both confirmed on all four calculators:

  1. `.quote-actions` - the container holding "Email Quote" and "Download Quote PDF" - is
     display:none in assets/ullc-calc.css, and NOTHING ever un-hides it. Grep for
     "quote-actions" across ullc-calc.js, blade.js, flat-top.js and perfmesh-calc.js returns
     ZERO hits. The rule also carries flex-direction:column, so it was written to be a flex
     column and shipped hidden. Both buttons have been in the DOM, wired up, and invisible.

  2. emailQuote() is an unfinished stub, byte-identical in all four calculator files:

         emailQuote(){ const email = prompt("Enter email address to send quote:");
                       email && alert("Quote would be emailed to: " + email) }

     It asks for the address and throws it away. Un-hiding the button without fixing this
     would collect emails into nothing, which is worse than hiding it.

  The consequence, from D1 clickfencing-crm.calc_events over 1-4 Sep: 116 calculator
  viewers, 35 reached a price, 8 saved a plan. Three of the four Google Ads conversion
  actions can only fire on /pages/quote-review, which you only reach by saving a plan - so
  Google Ads recorded 0 conversions in 90 days against 172 paid clicks a month. The tracking
  was never broken; it was watching a door almost nobody walks through.

  WHAT THIS DOES

  a) Fires "CF - Calculator price shown" (secondary, ONE_PER_CLICK, id 7746540611) the first
     time a real total appears. Uses the SAME #quote-breakdown selector and money regex as
     cf-calc-analytics.js, so "a price is shown" has one definition across the ledger and the
     ad account. Secondary on purpose - it is a milestone, not a lead, and must not be allowed
     to inflate CF - Quote completed the way PPF's "Submit lead form" inflated its account.

  b) Reveals .quote-actions once there IS a price, and relabels the button to
     "Email me this quote".

  c) Intercepts the #email-quote click in the CAPTURE phase and stopImmediatePropagation()s
     the stub, then stores the quote under cf_quote_for_enquiry and sends the customer to
     /pages/contact#ContactForm.

     That key and that destination are NOT new: cf-lead-capture.js already writes it from
     its quote-review button and already reads it back in prefillContact(). The contact form
     already mirrors to the CRM via the click-enquiry edge function (mirrorContactForm) and
     already fires CF - Enquiry submitted on the ?contact_posted=true return. So this reuses
     a lead path proven in production - the only two Click Fencing leads in 30 days that
     arrived with a phone number both came through "Contact Click Fencing | Send Us Your
     Plans" - instead of standing up a second one.

     Leaving the calculator is safe: cf-calc-memory.js snapshots every control into
     sessionStorage and replays it when the customer comes back.

  THE TOTAL IS REMEMBERED, NOT RE-READ ON CLICK
  Measured on the draft theme 2026-09-04: #quote-breakdown holds the money string when the
  price first renders, and by the time the customer clicks it has been re-rendered and no
  longer matches - the first build of this file sent them to the contact form with
  "Estimated total: $0.00". So the last non-zero total seen is kept and used, with a fresh
  read only as a fallback.

  2026-09-11 - THE TOTAL IS NOW THE SUM OF THE LINES PRINTED. IT UNDER-QUOTED BEFORE.
  Pete: "lets make sure the calculator doesnt under quote again." Two real enquiries on
  9 Sep 2026 read "Estimated total" LOWER than their own itemised lines: flat top $1,583.00
  against lines summing $1,759.65 (short exactly the last section), blade $4,364.51 against
  $5,588.58 (short the last section, a gate section). Reproduced on live 141617168478 the
  same way: 3-section flat top, gate last, calculator Final Quote $1,516.85, message said
  "Estimated total: $1014.31" over 13 lines summing $1,516.85.

  Cause: the old readTotal() took the FIRST "$" in #quote-breakdown's innerText, assuming
  it was the job total. It never was. That panel prints "Section 1 $x" (aluminium) or a
  category subtotal (glass) first, and on 2+ section jobs cf-current-section-parts.js
  collapses every other section into "Other sections (N) $y" - placed BEFORE the current
  section when the customer is on the last one. innerText skips the hidden sections, so the
  first "$" became "total minus the section you are on". The line list was read by
  querySelectorAll + textContent, which DOES include hidden rows, so it listed everything.
  Two reads, two different scopes, one message.

  Fix: readBreakdown() reads the lines ONCE, from the calculator's full breakdown
  (#breakdown-content-final on Review, where the button lives; #quote-breakdown otherwise),
  skips the .cf-cs-gen copies cf-current-section-parts.js makes for glass, and the total is
  the sum of exactly the lines it prints. The calculator's own .breakdown-total is read as
  a cross-check; if it ever disagrees by more than a cent the message says so rather than
  choosing one. No cap on line count any more - a 40-line cap would itself drop lines from
  the total on big jobs. Lines and total are always taken from the same snapshot.

  ONE OWNER: nothing else on the theme touches .quote-actions (0 references in all four
  calculator sources) or listens on #email-quote except the stub this replaces.
  #download-quote is left completely alone - its handler works, it was only ever hidden.

  No price, product, variantId, quantity or cart line is touched by any of this.
*/
(function () {
  'use strict';

  if (window.__cfQuoteCapture) return;
  window.__cfQuoteCapture = true;

  var AW = 'AW-18355899206';
  var PRICE_SHOWN_LABEL = 'JZ7ICMOo6-0cEMaW47BE'; // CF - Calculator price shown (secondary)
  var PLANS_LABEL = 'wIBhCKnHw-gcEMaW47BE';       // CF - Plans / help requested (secondary)
  var QUOTE_KEY = 'cf_quote_for_enquiry';         // read by cf-lead-capture.js prefillContact()
  var CONTACT_URL = '/pages/contact#ContactForm';

  // Same map as cf-calc-analytics.js, so both agree on what counts as a calculator page.
  var CALC_BY_PATH = {
    '/pages/glass-fencing': 'glass',
    '/pages/fence-calculator': 'glass',
    '/pages/blade-fence-calculator': 'blade',
    '/pages/flat-top-fence-calculator': 'flattop',
    '/pages/perfmesh-fence': 'perfmesh',
    '/pages/base-mount-batten-calculator': 'batten'
  };

  // Pete's rule: product names take capitals in anything a customer reads.
  var PRODUCT_NAME = {
    glass: 'Frameless Glass',
    blade: 'Blade',
    flattop: 'Flat Top',
    perfmesh: 'PerfMesh',
    batten: 'Base Mount Batten'
  };

  var path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  var CALC = CALC_BY_PATH[path];
  if (!CALC) return;

  function log() {
    try { console.log.apply(console, ['[cf-quote-capture]'].concat([].slice.call(arguments))); } catch (e) {}
  }
  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (err) { try { console.debug('[cf-quote-capture]', err); } catch (e) {} }
    };
  }

  // ---- gtag ---------------------------------------------------------------
  // gtag.js arrives via the Shopify Google & YouTube channel, so it is not
  // guaranteed to be present when this parses. Same 15s wait cf-lead-capture uses.
  function whenGtag(cb) {
    if (typeof window.gtag === 'function') { cb(); return; }
    var waited = 0;
    var iv = setInterval(function () {
      if (typeof window.gtag === 'function') { clearInterval(iv); cb(); return; }
      waited += 100;
      if (waited >= 15000) { clearInterval(iv); log('gtag never loaded - event dropped'); }
    }, 100);
  }

  var firedOnce = {};
  function fireConversion(key, label, params) {
    if (firedOnce[key]) return;
    firedOnce[key] = true;
    whenGtag(function () {
      try {
        window.gtag('event', 'conversion', {
          send_to: AW + '/' + label,
          value: (params && params.value) || 0,
          currency: 'AUD'
        });
        log('conversion sent', key, params || {});
      } catch (e) { log('conversion failed', key, e); }
    });
  }

  // ---- price detection ----------------------------------------------------
  // Identical selector + regex to cf-calc-analytics.js's price_shown step.
  var lastSnap = null;   // last full breakdown seen - see readBreakdown()

  // readTotal() is ONLY a "has a price appeared yet" signal for revealing the buttons and
  // the price-shown conversion. It is NOT the job total (see the 2026-09-11 note above) and
  // must never be printed to a customer.
  function readTotal() {
    var box = document.getElementById('quote-breakdown');
    if (!box) return 0;
    var m = (box.innerText || '').match(/\$\s?([\d,]+(?:\.\d{2})?)/);
    if (!m) return 0;
    var v = parseFloat(m[1].replace(/,/g, ''));
    return isFinite(v) && v > 0 ? v : 0;
  }

  // The LAST "$" figure in a piece of text, in cents. A breakdown row reads
  // "<name> Ã— <qty> ... $<unit> each $<line total>", so its last figure is the line total.
  function lastMoneyCents(text) {
    var re = /\$\s?([\d,]+(?:\.\d{1,2})?)/g, m, last = null;
    while ((m = re.exec(String(text || '')))) last = m[1];
    if (last == null) return null;
    var v = parseFloat(last.replace(/,/g, ''));
    return isFinite(v) ? Math.round(v * 100) : null;
  }

  // Review first - it is where #email-quote lives and it is never collapsed by
  // cf-current-section-parts.js. #quote-breakdown is the Configure-step fallback; every
  // section's rows are still in it there (hidden, not removed), so it is also whole-job.
  var BREAKDOWN_SOURCES = ['#breakdown-content-final', '#quote-breakdown-final', '#quote-breakdown'];

  function readBreakdown() {
    try {
      for (var s = 0; s < BREAKDOWN_SOURCES.length; s++) {
        var box = document.querySelector(BREAKDOWN_SOURCES[s]);
        if (!box) continue;
        var rows = box.querySelectorAll('.breakdown-item');
        if (!rows.length) rows = box.querySelectorAll('li, tr');
        var lines = [], sum = 0;
        for (var i = 0; i < rows.length; i++) {
          // Copies cf-current-section-parts.js builds for the glass Configure panel. The
          // originals are still there (hidden), so counting these would double a section.
          if (rows[i].closest && rows[i].closest('.cf-cs-gen')) continue;
          var t = (rows[i].textContent || '').replace(/\s+/g, ' ').trim();
          if (!t) continue;
          lines.push('  ' + t);
          sum += lastMoneyCents(t) || 0;
        }
        if (!lines.length) continue;
        var totEl = box.querySelector('.breakdown-total');
        return {
          lines: lines,
          sumCents: sum,
          shownCents: totEl ? lastMoneyCents(totEl.textContent) : null,
          source: BREAKDOWN_SOURCES[s]
        };
      }
    } catch (e) {}
    return null;
  }

  function dollars(cents) { return '$' + (cents / 100).toFixed(2); }

  // ---- quote summary for the contact form --------------------------------
  // Mirrors quoteSummary() in cf-lead-capture.js so the prefilled message reads
  // the same whichever page the customer came from.
  //
  // The total printed is the sum of the lines printed, from ONE snapshot - never a figure
  // read from somewhere else. With no readable lines, no figure is printed at all: a
  // missing number costs a phone call, a wrong one under-quotes a customer.
  function quoteSummary() {
    var snap = readBreakdown() || lastSnap;
    var lines = snap ? snap.lines : [];
    var cents = snap ? snap.sumCents : 0;
    var money = cents > 0 ? dollars(cents) : '';
    var body = [
      'Please check this quote and send me a fixed price.',
      '',
      'Fence type: ' + (PRODUCT_NAME[CALC] || CALC),
      money ? 'Estimated total: ' + money : 'Estimated total: (shown on screen)'
    ];
    if (snap && snap.shownCents != null && Math.abs(snap.shownCents - cents) > 1) {
      body.push('Note: the calculator showed a total of ' + dollars(snap.shownCents) +
                ', but the lines below add to ' + dollars(cents) + '. Click Fencing will confirm the price.');
      log('MISMATCH lines ' + dollars(cents) + ' vs calculator Total ' + dollars(snap.shownCents));
    }
    body.push('');
    if (lines.length) body.push('Breakdown as shown on the site:', lines.join('\n'), '');
    body.push('My questions / anything unusual about the site:', '');
    return { body: body.join('\n'), total: money, value: cents / 100 };
  }

  // ---- reveal + rewire ----------------------------------------------------
  var revealed = false;
  function revealActions(total) {
    if (revealed) return;
    var box = document.querySelector('.quote-actions');
    if (!box) return;
    revealed = true;

    // ullc-calc.css sets display:none with flex-direction:column - it was written
    // to be a flex column, so this restores the intended value rather than guessing.
    box.style.display = 'flex';
    box.setAttribute('data-cf-revealed', '1');

    var emailBtn = document.getElementById('email-quote');
    if (emailBtn) {
      emailBtn.textContent = 'ðŸ“§ Email me this quote';
      emailBtn.setAttribute('data-cf-capture', '1');
    }
    log('quote actions revealed at $' + total.toFixed(2));
  }

  // Capture phase, on document, so this runs BEFORE the calculator's own
  // bubble-phase listener and can stop the prompt()/alert() stub dead.
  document.addEventListener('click', safe(function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#email-quote') : null;
    if (!btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var summary = quoteSummary();
    try {
      sessionStorage.setItem(QUOTE_KEY, JSON.stringify({ body: summary.body, total: summary.total }));
    } catch (err) { log('could not stash the quote', err); }

    fireConversion('plans_open', PLANS_LABEL, { value: summary.value });

    /* 2026-10-13 (Pete: "fix the calculator send this quote button too"). This button used to go
       straight to the contact form with a text summary only, so the CRM got no quote row and no
       drawing (David Mogridge, 8.14am 13 Oct). Now it saves the plan through the calculator's OWN
       quote-page step (the same one Add to Cart / Get my quote uses - it saves and navigates, it
       never touches the cart) and flags quote-review, where cf-quote-enquiry-bridge.js builds the
       plan PDF and carries on to the contact form. If anything fails, the old route still runs. */
    var calc = window.glassCalculator;
    var step = calc && (CALC === 'perfmesh' ? calc.addToCart : calc.proceedToQuotePage);
    if (typeof step === 'function') {
      try {
        sessionStorage.setItem('cf_send_after_review', String(Date.now()));
        setTimeout(function () { step.call(calc); }, 250);
        // Still here after 4s (the step refused or threw) - fall back to the contact form.
        setTimeout(function () {
          try { sessionStorage.removeItem('cf_send_after_review'); } catch (x) {}
          location.href = CONTACT_URL;
        }, 4000);
        return;
      } catch (err) { log('quote-page step failed, using the contact form', err); }
    }

    // Give the conversion beacon a moment, but never let it hold the customer up.
    setTimeout(function () { location.href = CONTACT_URL; }, 250);
  }), true);

  // ---- watch for the price -----------------------------------------------
  function check() {
    var total = readTotal();
    if (!total) return;
    var snap = readBreakdown();
    if (snap) lastSnap = snap;
    revealActions(total);
    fireConversion('price_shown', PRICE_SHOWN_LABEL, { value: total });
  }

  function start() {
    check();
    try {
      var obs = new MutationObserver(safe(check));
      obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch (e) {
      setInterval(safe(check), 1000); // observers unavailable - poll instead
    }
    log('installed for "' + CALC + '"');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
